import { AlertTriangle } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  PRELIMINARY_LABEL,
  PRELIMINARY_MICRO,
  PRELIMINARY_SHORT,
  tierDisclaimerNote,
  type DisclaimerTier,
} from "@/lib/legal-text";

/**
 * Shared "these are preliminary results" framing.
 *
 * Every surface that shows a computed result — reports, previews, shared
 * links, map sections, feasibility cards — renders one of these so the wording
 * is identical on screen and on paper.
 */
export function PreliminaryNotice({
  variant = "inline",
  tier,
  className,
}: {
  /** `badge` for a compact chip next to a heading, `inline` for a full line. */
  variant?: "badge" | "inline";
  /** When set, the tier-specific sentence replaces the generic one. */
  tier?: DisclaimerTier | null | undefined;
  className?: string;
}) {
  const body = tier ? tierDisclaimerNote(tier) : PRELIMINARY_SHORT;
  if (variant === "badge") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full border border-amber-700/30 bg-amber-950/90 px-2 py-0.5 text-[11px] font-medium text-amber-100",
          className,
        )}
        title={body}
      >
        <AlertTriangle className="size-3 shrink-0" aria-hidden="true" />
        {PRELIMINARY_LABEL}
      </span>
    );
  }

  return (
    <p
      className={cn(
        "flex items-start gap-2 rounded-md border border-amber-700/25 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-muted-foreground",
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" aria-hidden="true" />
      <span>
        <strong className="font-semibold text-foreground">{PRELIMINARY_LABEL}.</strong> {body}
      </span>
    </p>
  );
}

export { PRELIMINARY_LABEL, PRELIMINARY_MICRO, PRELIMINARY_SHORT };
